import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, timer, TimeoutError } from 'rxjs';
import { timeout, catchError, retryWhen, mergeMap } from 'rxjs/operators';

@Injectable()
export class TimeoutInterceptor implements HttpInterceptor {
  // Default timeout: 60 seconds for large pattern saves
  private readonly DEFAULT_TIMEOUT = 60000;

  // Longer timeout for pattern save operations
  private readonly PATTERN_SAVE_TIMEOUT = 120000; // 2 minutes

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Determine timeout based on endpoint
    let timeoutDuration = this.DEFAULT_TIMEOUT;

    if (req.url.includes('/pattern/savepattern') || req.url.includes('/pattern/transferimage')) {
      // Pattern operations need more time
      timeoutDuration = this.PATTERN_SAVE_TIMEOUT;
    }

    return next.handle(req).pipe(
      timeout(timeoutDuration),
      retryWhen(errors =>
        errors.pipe(
          mergeMap((error, index) => {
            // Only retry on network errors (status 0) and timeouts
            // Don't retry on 4xx/5xx errors
            const shouldRetry =
              index < 1 && // Max 1 retry
              (error.status === 0 || error instanceof TimeoutError);

            if (shouldRetry) {
              // Wait 2 seconds before retrying
              return timer(2000);
            }

            return throwError(error);
          })
        )
      ),
      catchError((error: any) => {
        // Enhance error message for better debugging
        if (error instanceof TimeoutError) {
          // Timeout error
          const enhancedError = new HttpErrorResponse({
            error: { message: 'Request timed out. Please try again.' },
            headers: undefined,
            status: 408, // Request Timeout
            statusText: 'Request Timeout',
            url: req.url,
          });
          return throwError(enhancedError);
        } else if (error.status === 0) {
          // Network error or CORS issue
          const enhancedError = new HttpErrorResponse({
            error: error.error,
            headers: error.headers,
            status: error.status,
            statusText: 'Network Error - Connection lost or timeout',
            url: error.url,
          });
          return throwError(enhancedError);
        }

        return throwError(error);
      })
    );
  }
}
